import { useState } from 'react'
import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  Spinner,
  Alert,
  useDisclosure,
  Heading,
  Badge,
  Collapsible,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { extractApiMessage } from '../lib/apiError'
import { FollowUpKindsManager } from '../components/surveys/FollowUpKindsManager'
import { useRestoreStarterQuestions } from '../hooks/useFollowUp'
import { FiPlus, FiEdit2, FiChevronDown, FiChevronUp } from 'react-icons/fi'
import { CanvassingPageShell } from '../components/canvassing/CanvassingPageShell'
import { SurveyGroupCard } from '../components/surveys/SurveyGroupCard'
import { SurveyGroupFormModal } from '../components/surveys/SurveyGroupFormModal'
import { SurveyQuestionFormModal } from '../components/surveys/SurveyQuestionFormModal'
import { SurveySearchForm, type SurveySearchFilters } from '../components/surveys/SurveySearchForm'
import { SurveySearchPagination } from '../components/surveys/SurveySearchPagination'
import { useSurveyGroups, useSurveyQuestionSearch, useSurveyQuestionDetail } from '../hooks/useSurvey'
import { ResponsiveTable, type ResponsiveColumn } from '../components/shared'
import type {
  SurveyGroupSummaryDto,
  SurveyQuestionSearchRequest,
  SurveyQuestionSummaryDto,
} from '../types/survey'
import { SURVEY_TYPE_LABELS } from '../types/survey'

export function CanvassingSurveysPage() {
  const { data: groups, isLoading: groupsLoading } = useSurveyGroups()

  // Search state
  const [searchParams, setSearchParams] = useState<SurveyQuestionSearchRequest>({
    page: 1,
    pageSize: 25,
  })
  const [hasSearched, setHasSearched] = useState(true) // Auto-search on mount

  // Search query
  const {
    data: searchResults,
    isLoading: questionsLoading,
    error: questionsError,
  } = useSurveyQuestionSearch(searchParams, hasSearched)

  // Modal state
  const {
    open: isGroupModalOpen,
    onOpen: onGroupModalOpen,
    onClose: onGroupModalClose,
  } = useDisclosure()

  const {
    open: isQuestionModalOpen,
    onOpen: onQuestionModalOpen,
    onClose: onQuestionModalClose,
  } = useDisclosure()

  const [selectedGroup, setSelectedGroup] = useState<SurveyGroupSummaryDto | null>(null)
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null)

  // Fetch full question details when editing
  const { data: selectedQuestion } = useSurveyQuestionDetail(selectedQuestionId || undefined)

  // Groups section collapse state
  const [isGroupsSectionOpen, setIsGroupsSectionOpen] = useState(false)
  const [isKindsSectionOpen, setIsKindsSectionOpen] = useState(false)

  const restoreStarters = useRestoreStarterQuestions()
  const handleRestoreStarters = () =>
    restoreStarters.mutate(undefined, {
      onSuccess: ({ created }) =>
        toaster.create({
          title: 'Starter questions restored',
          description: created === 0 ? 'All starter questions were already present.' : `Restored ${created} starter question${created === 1 ? '' : 's'}.`,
          type: 'success',
        }),
      onError: (err) =>
        toaster.create({ title: 'Error', description: extractApiMessage(err, 'Failed to restore starter questions.'), type: 'error' }),
    })

  const handleCreateGroup = () => {
    setSelectedGroup(null)
    onGroupModalOpen()
  }

  const handleEditGroup = (group: SurveyGroupSummaryDto) => {
    setSelectedGroup(group)
    onGroupModalOpen()
  }

  const handleCreateQuestion = () => {
    setSelectedQuestionId(null)
    onQuestionModalOpen()
  }

  const handleEditQuestion = (questionId: string) => {
    setSelectedQuestionId(questionId)
    onQuestionModalOpen()
  }

  const handleCloseGroupModal = () => {
    setSelectedGroup(null)
    onGroupModalClose()
  }

  const handleCloseQuestionModal = () => {
    setSelectedQuestionId(null)
    onQuestionModalClose()
  }

  // Search handlers
  const handleSearch = (filters: SurveySearchFilters) => {
    setSearchParams({
      ...filters,
      page: 1, // Reset to page 1 on new search
      pageSize: searchParams.pageSize,
    })
    setHasSearched(true)
  }

  const handleClear = () => {
    setSearchParams({
      page: 1,
      pageSize: searchParams.pageSize,
    })
    setHasSearched(true)
  }

  const handlePageChange = (newPage: number) => {
    setSearchParams(prev => ({ ...prev, page: newPage }))
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setSearchParams(prev => ({
      ...prev,
      page: 1, // Reset to page 1 when changing page size
      pageSize: newPageSize,
    }))
  }

  const isLoading = groupsLoading || questionsLoading
  const error = questionsError

  const questions = searchResults?.items || []
  const hasQuestions = questions.length > 0

  const columns: ResponsiveColumn<SurveyQuestionSummaryDto>[] = [
    {
      header: 'Label',
      mobileRole: 'title',
      cell: (q) => (
        <HStack gap={2}>
          <Text fontWeight="medium" fontSize="sm">
            {q.label}
          </Text>
          {q.starterKey && <Badge bg="accent.soft" color="accent" fontSize="xs">Starter</Badge>}
        </HStack>
      ),
    },
    {
      header: 'Group',
      mobileRole: 'badge',
      cell: (q) =>
        q.surveyGroupId ? (
          <Badge bg="info.subtle" color="info.fg" fontSize="xs">
            {q.surveyGroupLabel}
          </Badge>
        ) : (
          <Text fontSize="xs" color="gray.500">
            (No Group)
          </Text>
        ),
    },
    {
      header: 'Question',
      mobileRole: 'meta',
      cell: (q) => (
        <Text fontSize="sm" lineClamp={2}>
          {q.question}
        </Text>
      ),
    },
    {
      header: 'Type',
      mobileRole: 'badge',
      cell: (q) => (
        <Badge colorPalette="purple" fontSize="xs">
          {SURVEY_TYPE_LABELS[q.type]}
        </Badge>
      ),
    },
    {
      header: 'Cycle',
      mobileRole: 'badge',
      cell: (q) => (
        <Badge colorPalette="gray" fontSize="xs">
          {q.cycle}
        </Badge>
      ),
    },
    {
      header: 'Answers',
      mobileRole: 'meta',
      cell: (q) => <Text fontSize="sm">{q.answerCount}</Text>,
    },
    {
      header: 'Status',
      mobileRole: 'badge',
      cell: (q) => (
        <Badge colorPalette={q.isActive ? 'green' : 'red'} fontSize="xs">
          {q.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ]

  return (
    <CanvassingPageShell
      title="Surveys"
      subtitle="Manage survey questions and question groups for canvassing"
    >
      <VStack gap={6} align="stretch">
        <HStack justify="flex-end" gap={3} wrap="wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={handleRestoreStarters}
            loading={restoreStarters.isPending}
            disabled={restoreStarters.isPending}
          >
            Restore starter questions
          </Button>
          <Button
            colorPalette="brand"
            size="sm"
            onClick={handleCreateGroup}
            variant="outline"><FiPlus />Create Group
                      </Button>
          <Button colorPalette="brand" size="sm" onClick={handleCreateQuestion}><FiPlus />Create Question
                      </Button>
        </HStack>

        {isLoading && (
          <VStack py={12}>
            <Spinner size="lg" color="accent" />
            <Text color="gray.500">Loading surveys...</Text>
          </VStack>
        )}

        {!!error && (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            Failed to load surveys. Please try again.
          </Alert.Root>
        )}

        {/* Search Form */}
        <SurveySearchForm onSearch={handleSearch} onClear={handleClear} />

        {/* Loading State */}
        {isLoading && (
          <VStack py={12}>
            <Spinner size="lg" color="accent" />
            <Text color="gray.500">Loading questions...</Text>
          </VStack>
        )}

        {/* Error State */}
        {!!error && (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            Failed to load questions. Please try again.
          </Alert.Root>
        )}

        {/* Results */}
        {!isLoading && hasSearched && searchResults && (
          <>
            {hasQuestions ? (
              <>
                <Box>
                  <Heading size="sm" mb={4}>
                    Survey Questions
                  </Heading>
                  <ResponsiveTable
                    columns={columns}
                    rows={questions}
                    keyFn={(q) => q.id}
                    rowActions={[
                      {
                        label: 'Edit',
                        icon: FiEdit2,
                        onClick: (q) => handleEditQuestion(q.id),
                      },
                    ]}
                  />
                </Box>

                {/* Pagination */}
                <SurveySearchPagination
                  data={searchResults}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              </>
            ) : (
              <Box
                bg="surface"
                p={12}
                borderRadius="lg"
                shadow="md"
                textAlign="center"
              >
                <Text color="gray.500" mb={4}>
                  No survey questions found
                </Text>
                <HStack justify="center" gap={3}>
                  <Button
                    colorPalette="brand"
                    size="sm"
                    onClick={handleCreateGroup}
                    variant="outline"><FiPlus />Create Group
                                      </Button>
                  <Button colorPalette="brand" size="sm" onClick={handleCreateQuestion}><FiPlus />Create Question
                                      </Button>
                </HStack>
              </Box>
            )}
          </>
        )}

        {/* Follow-up kinds manager */}
        <Box mt={4}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsKindsSectionOpen(!isKindsSectionOpen)}
            mb={4}><Heading size="sm">Follow-up kinds</Heading>{isKindsSectionOpen ? <FiChevronUp /> : <FiChevronDown />}</Button>
          <Collapsible.Root open={isKindsSectionOpen}>
            <Collapsible.Content>
              <FollowUpKindsManager />
            </Collapsible.Content>
          </Collapsible.Root>
        </Box>

        {/* Survey Groups - Collapsible Secondary View */}
        {groups && groups.length > 0 && (
          <Box mt={8}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsGroupsSectionOpen(!isGroupsSectionOpen)}
              mb={4}><Heading size="sm">Survey Groups ({groups.length})</Heading>{isGroupsSectionOpen ? <FiChevronUp /> : <FiChevronDown />}</Button>
            <Collapsible.Root open={isGroupsSectionOpen}>
              <Collapsible.Content>
                <VStack gap={4} align="stretch">
                  {groups.map((group) => (
                    <SurveyGroupCard
                      key={group.id}
                      group={group}
                      onEditGroup={handleEditGroup}
                      onEditQuestion={handleEditQuestion}
                    />
                  ))}
                </VStack>
              </Collapsible.Content>
            </Collapsible.Root>
          </Box>
        )}
      </VStack>

      <SurveyGroupFormModal
        isOpen={isGroupModalOpen}
        onClose={handleCloseGroupModal}
        group={selectedGroup}
      />

      <SurveyQuestionFormModal
        isOpen={isQuestionModalOpen}
        onClose={handleCloseQuestionModal}
        question={selectedQuestion}
      />
    </CanvassingPageShell>
  );
}
